import { PerpsProvider } from '@kwenta/sdk/types'
import BlogIcon from 'assets/svg/nav/blog.svg'
import DiscordLogo from 'assets/svg/nav/discord.svg'
import DocsIcon from 'assets/svg/nav/docs.svg'
import ExchangeActiveIcon from 'assets/svg/nav/exchange-active.svg'
import GovernanceIcon from 'assets/svg/nav/governance.svg'
import LeverageIcon from 'assets/svg/nav/leverage.svg'
import StakeIcon from 'assets/svg/nav/stake.svg'
import StatsIcon from 'assets/svg/nav/stats.svg'
import VaultsIcon from 'assets/svg/nav/vaults.svg'
import XLogo from 'assets/svg/nav/x.svg'
import BaseIcon from 'assets/svg/providers/base.svg'
import OptimismIcon from 'assets/svg/providers/optimism.svg'
import { DEFAULT_PERPS_PROVIDER, SNX_V2_ENABLED, SNX_V3_BASE_ENABLED } from 'constants/futures'
import { EXTERNAL_LINKS } from 'constants/links'
import ROUTES from 'constants/routes'
import type { ReactNode } from 'react'
import { HistoryTabs } from 'sections/dashboard/History/HistoryTabs'
import { MarketTabs } from 'sections/dashboard/Markets/MarketsTab'
import { Tab } from 'types/dashboard'

export enum SubMenuItemType {
	DEFAULT = 'Futures',
	BASE = 'Futures - Base',
	OPTIMISM = 'Futures - Optimism',
	DASHBOARD = 'Dashboard',
	SWAPS = 'Swaps',
}

type Badge = {
	i18nLabel: string
	color: 'yellow' | 'red' | 'gray'
}

export type NetworkItem = {
	icon: ReactNode
	name: string
	provider: PerpsProvider
	category: 'isolated-margin' | 'cross-margin'
	hasRewards?: boolean
	enabled: boolean
}

export type SubMenuLink = {
	i18nLabel: string
	link: string
	badge?: Badge[]
	icon?: ReactNode
	externalLink?: boolean
	type?: SubMenuItemType
	tabs?: SubMenuLink[]
	tabType?: Tab
}

export type AppItem = {
	icon: ReactNode
	label: string
	copy?: string
	href: string
	isActive?: boolean
	subMenu?: SubMenuLink[]
}

export const NavDropdowns = {
	Futures: 'futures',
	Collections: 'collections',
} as const

export type NavDropdownType = (typeof NavDropdowns)[keyof typeof NavDropdowns]

type NavDropdown = {
	type: NavDropdownType
	items: NetworkItem[] | AppItem[]
}

type MenuLink = {
	i18nLabel: string
	link: string
	dropdown?: NavDropdown
	links?: SubMenuLink[] | null
	hidden?: boolean
}

type MenuLinks = MenuLink[]

export const HOMEPAGE_MENU_LINKS: MenuLinks = [
	{
		i18nLabel: 'homepage.nav.markets',
		link: SNX_V3_BASE_ENABLED
			? ROUTES.Markets.Home(PerpsProvider.SNX_V3_BASE)
			: ROUTES.Markets.Home(PerpsProvider.SNX_V2_OP),
	},
	{
		i18nLabel: 'homepage.nav.stats',
		link: ROUTES.Stats.Home,
	},
]

const DASHBOARD_LINKS = [
	{
		link: ROUTES.Dashboard.Home,
		i18nLabel: 'dashboard.tabs.overview',
		tabType: Tab.Overview,
	},
	{
		link: ROUTES.Dashboard.History(HistoryTabs.POSITIONS),
		i18nLabel: 'dashboard.tabs.history',
		tabType: Tab.History,
	},
	{
		link: ROUTES.Dashboard.Markets(MarketTabs.ALL_MARKETS),
		i18nLabel: 'dashboard.tabs.markets',
		tabType: Tab.Markets,
	},
	{
		link: ROUTES.Dashboard.Leaderboard,
		i18nLabel: 'dashboard.tabs.leaderboard',
		tabType: Tab.Leaderboard,
	},
]

const getMenuLinks = (isMobile: boolean): MenuLinks => [
	{
		i18nLabel: 'header.nav.markets',
		link: ROUTES.Markets.Home(DEFAULT_PERPS_PROVIDER),
		dropdown: {
			type: NavDropdowns.Futures,
			items: [
				{
					name: 'header.nav.dropdown.optimism',
					icon: <OptimismIcon width={17} height={17} />,
					provider: PerpsProvider.SNX_V2_OP,
					category: 'isolated-margin',
					hasRewards: true,
					enabled: SNX_V2_ENABLED,
				},
				{
					name: 'header.nav.dropdown.base',
					icon: <BaseIcon width={17} height={17} />,
					provider: PerpsProvider.SNX_V3_BASE,
					category: 'cross-margin',
					enabled: SNX_V3_BASE_ENABLED,
				},
			],
		},
	},
	{
		i18nLabel: 'header.nav.dashboard',
		link: ROUTES.Dashboard.Home,
		links: isMobile ? DASHBOARD_LINKS : null,
	},
	{
		i18nLabel: 'header.nav.staking',
		link: ROUTES.Staking.Home,
	},
	{
		i18nLabel: 'header.nav.rewards',
		link: ROUTES.Rewards.Home,
		hidden: true,
	},
	{
		i18nLabel: 'header.nav.swaps',
		link: ROUTES.Swaps.Home,
	},
]

export const DESKTOP_NAV_LINKS = getMenuLinks(false).filter((m) => !m.hidden)
export const MOBILE_NAV_LINKS = getMenuLinks(true).filter((m) => !m.hidden)

const apps: AppItem[] = [
	{
		label: 'header.nav.super-app.stake.title',
		copy: 'header.nav.super-app.stake.copy',
		icon: <StakeIcon width={20} height={20} />,
		href: EXTERNAL_LINKS.App.Stake,
	},
	{
		label: 'header.nav.super-app.exchange.title',
		copy: 'header.nav.super-app.exchange.copy',
		isActive: true,
		icon: <ExchangeActiveIcon width={20} height={20} />,
		href: ROUTES.Markets.Home(DEFAULT_PERPS_PROVIDER),
		subMenu: [
			{
				i18nLabel: 'header.nav.sub-menu.futures',
				link: ROUTES.Markets.Home(DEFAULT_PERPS_PROVIDER),
				type: SubMenuItemType.DEFAULT,
			},
			{
				i18nLabel: 'header.nav.sub-menu.base',
				link: ROUTES.Markets.Home(PerpsProvider.SNX_V3_BASE),
				type: SubMenuItemType.BASE,
			},
			{
				i18nLabel: 'header.nav.sub-menu.optimism',
				link: ROUTES.Markets.Home(PerpsProvider.SNX_V2_OP),
				type: SubMenuItemType.OPTIMISM,
			},
			{
				i18nLabel: 'header.nav.sub-menu.dashboard',
				link: ROUTES.Dashboard.Home,
				type: SubMenuItemType.DASHBOARD,
				tabs: DASHBOARD_LINKS,
			},
			{
				i18nLabel: 'header.nav.sub-menu.swaps',
				link: ROUTES.Swaps.Home,
				type: SubMenuItemType.SWAPS,
			},
		],
	},
	{
		label: 'header.nav.super-app.vaults.title',
		copy: 'header.nav.super-app.vaults.copy',
		icon: <VaultsIcon width={20} height={20} />,
		href: EXTERNAL_LINKS.App.Vaults,
	},
	{
		label: 'header.nav.super-app.leverage.title',
		copy: 'header.nav.super-app.leverage.copy',
		icon: <LeverageIcon width={20} height={20} />,
		href: EXTERNAL_LINKS.App.Leverage,
	},
	{
		label: 'header.nav.footer.docs',
		icon: <DocsIcon />,
		href: EXTERNAL_LINKS.Docs.SnxDocs,
	},
	{
		label: 'header.nav.footer.blog',
		icon: <BlogIcon />,
		href: EXTERNAL_LINKS.Social.Blog,
	},
	{
		label: 'header.nav.footer.governance',
		icon: <GovernanceIcon />,
		href: EXTERNAL_LINKS.Governance.Home,
	},
	{
		label: 'header.nav.footer.stats',
		icon: <StatsIcon width={16} height={16} />,
		href: ROUTES.Stats.Home,
	},
	{
		label: 'header.nav.footer.discord',
		icon: <DiscordLogo width={16} height={16} />,
		href: EXTERNAL_LINKS.Social.Discord,
	},
	{
		label: 'header.nav.footer.x-com',
		icon: <XLogo width={16} height={16} />,
		href: EXTERNAL_LINKS.Social.Twitter,
	},
]

export const CROSS_APP_LINKS: MenuLinks = [
	{
		i18nLabel: '',
		link: '',
		dropdown: {
			type: NavDropdowns.Collections,
			items: apps,
		},
	},
]
